import React, { useState, useEffect } from 'react';
import { fetchData, postData, Base64 } from '../components/misc';
import { trackEvent } from '../utils/bridge.js';

export default function SupportChatAdmin() {
    const [chats, setChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [selectedName, setSelectedName] = useState('');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [file, setFile] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        loadChats(controller.signal);
        return () => controller.abort();
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        if (selectedChat) {
            loadMessages(selectedChat.ticket, controller.signal);
            const encoded = Base64.encode(selectedChat.email);
            fetchData(`/api/getprofile/${encoded}`, { signal: controller.signal })
                .then(data => {
                    if (data && data.responsecode === 200) {
                        const profile = data.data || {};
                        let name = profile.fullname || profile.fullname1 || '';
                        if (!name) {
                            name = `${profile.firstname1 || ''} ${profile.middlename1 || ''} ${profile.lastname1 || ''}`.trim();
                        }
                        setSelectedName(name);
                    } else {
                        setSelectedName('');
                    }
                })
                .catch(() => setSelectedName(''));
        } else {
            setSelectedName('');
        }
        return () => controller.abort();
    }, [selectedChat]);

    const loadChats = (signal) => {
        fetchData('/api/support/list_chats', { signal })
            .then(data => setChats(data.chats || []))
            .catch(err => console.error('Failed to load chats', err));
    };

    const loadMessages = (ticket, signal) => {
        fetchData(`/api/support/get_messages?ticket=${encodeURIComponent(ticket)}`, { signal })
            .then(data => setMessages(data.messages || []))
            .catch(err => console.error('Failed to load messages', err));
    };

    const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const sendMessage = async () => {
        if (!input && !file) return;
        if (!selectedChat) return;
        let imageData = null;
        if (file) {
            imageData = await toBase64(file);
            setFile(null);
        }
        trackEvent('admin_support_message_send_pressed')
        postData('/api/support/send_message', {
            email: selectedChat.email,
            ticket: selectedChat.ticket,
            message: input,
            image: imageData,
            sender: 'admin'
        }).then(() => {
            trackEvent('admin_support_message_sent')
            setInput('');
            loadMessages(selectedChat.ticket);
        }).catch(err => console.error('Failed to send message', err));
    };

    const resolveChat = () => {
        if (!selectedChat) return;
        trackEvent('admin_support_chat_resolved')
        postData('/api/support/resolve', { email: selectedChat.email, ticket: selectedChat.ticket })
            .then(() => {
                setSelectedChat(null);
                setMessages([]);
                loadChats();
            })
            .catch(err => console.error('Failed to resolve chat', err));
    };

    const deleteChat = () => {
        if (!selectedChat) return;
        trackEvent('admin_support_chat_deleted')
        postData('/api/support/delete', { email: selectedChat.email, ticket: selectedChat.ticket })
            .then(() => {
                setSelectedChat(null);
                setMessages([]);
                loadChats();
            })
            .catch(err => console.error('Failed to delete chat', err));
    };

    return (
        <div className="flex h-screen">
            <div className="w-1/3 border-r overflow-y-auto">
                {chats.map((c, idx) => (
                    <div key={idx}
                        className={`p-2 cursor-pointer ${selectedChat && selectedChat.ticket===c.ticket ? 'bg-gray-300' : ''}`}
                        onClick={() => { trackEvent('admin_support_chat_selected'); setSelectedChat(c); }}>
                        <div className="font-bold">{c.email}</div>
                        <div className="text-xs">Ticket: {c.ticket}</div>
                        {c.phone && (
                            <div className="text-xs">Phone: {c.phone}</div>
                        )}
                        <div className="text-xs">{c.resolved ? 'Resolved' : 'Open'}</div>
                        {c.last_time && (
                            <div className="text-xs text-gray-500">
                                {new Date(c.last_time * 1000).toLocaleString()}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="flex-1 flex flex-col p-4">
                {selectedChat ? (
                    <>
                        <div className="mb-2">
                            <div className="font-bold">{selectedChat.email}</div>
                            {selectedName && (
                                <div className="text-sm text-gray-600">{selectedName}</div>
                            )}
                            <div className="text-xs">Ticket: {selectedChat.ticket}</div>
                            {selectedChat.phone && (
                                <div className="text-xs">Phone: {selectedChat.phone}</div>
                            )}
                        </div>
                        <div className="flex-1 overflow-y-auto mb-2">
                            {messages.map((m, idx) => (
                                <div key={idx} className={m.from==='user' ? 'text-right' : 'text-left'}>
                                    <div className="text-xs text-gray-500">
                                        {m.time ? new Date(m.time * 1000).toLocaleString() : ''}
                                    </div>
                                    {m.from === 'user' && (
                                        <>
                                            <div className="text-xs text-gray-400">{selectedChat.email}</div>
                                            {selectedName && (
                                                <div className="text-xs text-gray-400">{selectedName}</div>
                                            )}
                                        </>
                                    )}
                                    <span className={m.from==='admin' ? 'inline-block bg-green-600 text-white px-2 py-1 rounded my-1' : 'inline-block bg-gray-200 px-2 py-1 rounded my-1'}>
                                        {m.message}
                                    </span>
                                    {m.image && (
                                        <img src={m.image} alt="attachment" className="max-w-xs max-h-60 inline-block my-1" />
                                    )}
                                </div>
                            ))}
                        </div>
                        <input
                            className="border p-2 mb-2"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            placeholder="Type a message"/>
                        <input type="file" className="mb-2" onChange={e => setFile(e.target.files[0])} />
                        <div className="flex gap-2">
                            <button className="bg-blue-600 text-white px-3 py-1" onClick={sendMessage}>Send</button>
                            <button className="bg-green-600 text-white px-3 py-1" onClick={resolveChat}>Resolve</button>
                            <button className="bg-red-600 text-white px-3 py-1" onClick={deleteChat}>Delete</button>
                        </div>
                    </>
                ) : (
                    <p>Select a chat to view messages</p>
                )}
            </div>
        </div>
    );
}


